import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    AppBar,
    Avatar,
    Box,
    ClickAwayListener,
    Fade,
    Paper,
    Popper,
    Tab,
    Tabs,
    Toolbar,
    Typography,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { SpotlightSearch } from 'components';
import ProfileOptions, { ProfileOptionsHandle } from 'components/Navigation/ProfileOptions';
import Logo from '../../../src/images/1Tul-logo.png';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import useNavigationTabConfig, { NavigationTabConfigProps } from './navigationconfig';
import { useNavigationContext } from './NavigationContext';
import SecondaryNavigation from './SecondaryNavigation';
import { OrganizationsContext } from '../../contexts/OrganizationsContext';
import { useSelector } from 'react-redux';
import {
    primaryOrganizationUuidSelector,
    primarySportSelector,
    userSelector,
    getOrganizationsWithAdminOrCoachRoles,
    setPrimaryOrganization,
} from '../../redux/reducers/user';
import { useAppDispatch } from '../../redux/store';
import updatePrimaryOrganization from '../../api/Auth/updatePrimaryOrganization';
import PrimarySportQuickSelect from './PrimarySportQuickSelect';

const SPORT_DISPLAY_NAMES: Record<string, string> = {
    baseball: 'Baseball',
    softball: 'Softball',
    soccer: 'Soccer',
    football: 'Football',
    track: 'Track',
    multi_sport: 'Multi-Sport',
};

const DesktopTopNavigation = () => {
    const { push } = useHistory();
    const dispatch = useAppDispatch();
    const { setPrimaryNavigation, primaryNavigation } = useNavigationContext();
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const orgPickerAnchorRef = useRef<HTMLDivElement>(null);
    const profileOptionsRef = useRef<ProfileOptionsHandle>(null);
    const navigationTabConfig = useNavigationTabConfig();
    const organizationsContext = useContext(OrganizationsContext);
    const primaryOrganizationUuid = useSelector(primaryOrganizationUuidSelector);
    const primarySport = useSelector(primarySportSelector);
    const shouldPromptPrimarySport = !primarySport;
    const userData = useSelector(userSelector);
    const adminOrCoachOrgs = useSelector(getOrganizationsWithAdminOrCoachRoles);

    // Determine if user is a Coach or Athlete
    const userRoleLabel = useMemo(() => {
        const organizations = organizationsContext?.organizations || [];
        const isCoach = (adminOrCoachOrgs || []).some((team) => {
            const matchingOrg = organizations.find((org) => org.uuid === team.name);
            return matchingOrg && matchingOrg.type !== 'personal';
        });
        const role = isCoach ? 'Coach' : 'Athlete';
        const sportName = primarySport ? SPORT_DISPLAY_NAMES[primarySport] : null;
        return sportName ? `${sportName} ${role}` : role;
    }, [adminOrCoachOrgs, organizationsContext, primarySport]);

    const userName = useMemo(() => {
        const user = userData?.userData;
        if (user?.first_name && user?.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        return user?.first_name || user?.user_name || '';
    }, [userData]);

    // Find the primary organization
    const primaryOrganization = useMemo(() => {
        const organizations = organizationsContext?.organizations || [];
        if (!primaryOrganizationUuid || !organizations || organizations.length === 0) {
            return null;
        }
        const found = organizations.find((org) => org.uuid === primaryOrganizationUuid);
        return found || null;
    }, [primaryOrganizationUuid, organizationsContext]);

    // Non-personal organizations available for selection
    const selectableOrganizations = useMemo(() => {
        const organizations = organizationsContext?.organizations || [];
        return organizations.filter((org) => org.type !== 'personal');
    }, [organizationsContext]);

    const hasMultipleOrgs = selectableOrganizations.length > 1;

    // Auto-set primary org when user has exactly one non-personal org
    useEffect(() => {
        if (!primaryOrganizationUuid && selectableOrganizations.length === 1) {
            const singleOrg = selectableOrganizations[0];
            dispatch(setPrimaryOrganization(singleOrg.uuid));
            updatePrimaryOrganization(singleOrg.uuid).catch(() => {});
        }
    }, [primaryOrganizationUuid, selectableOrganizations, dispatch]);

    const handleSelectOrganization = useCallback(
        (orgUuid: string) => {
            dispatch(setPrimaryOrganization(orgUuid));
            updatePrimaryOrganization(orgUuid).catch(() => {
                // Silently handle - Redux state is already updated
            });
            setOrgPickerOpen(false);
        },
        [dispatch],
    );

    return (
        <>
            <AppBar position={'relative'} component="header">
                <Toolbar style={{ justifyContent: 'space-between' }} variant="dense">
                    <Box display="flex" alignItems="center" style={{ gap: 20 }} flexBasis="33%">
                        <Link to="/home/sprint-dashboard">
                            <img src={Logo} alt="1TUL Logo" width={70} />
                        </Link>
                        <SpotlightSearch />
                    </Box>
                    <Box flexBasis="33%" alignItems="center" justifyContent="center" display="flex">
                        <Tabs
                            indicatorColor="primary"
                            value={primaryNavigation ? primaryNavigation : false}
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'common.white',
                                },
                            }}
                        >
                            {navigationTabConfig.map(
                                (
                                    secondaryNavigationTab: NavigationTabConfigProps,
                                    index: number,
                                ) => (
                                    <Tab
                                        key={`secondary-navigation-tab-${index}`}
                                        value={secondaryNavigationTab.value}
                                        sx={{
                                            width: 80,
                                            minWidth: 'inherit',
                                            minHeight: 'inherit',
                                            color: '#4083ff',
                                            paddingTop: 8,
                                            '&.Mui-selected': {
                                                color: 'common.white',
                                            },
                                            '& .MuiTypography-root': {
                                                fontWeight: 600,
                                            },
                                        }}
                                        label={
                                            <Typography variant="caption">
                                                {secondaryNavigationTab.label}
                                            </Typography>
                                        }
                                        icon={
                                            secondaryNavigationTab.badgeContent ? (
                                                <Badge
                                                    badgeContent={
                                                        secondaryNavigationTab.badgeContent
                                                    }
                                                    color="error"
                                                >
                                                    <secondaryNavigationTab.icon />
                                                </Badge>
                                            ) : (
                                                <secondaryNavigationTab.icon />
                                            )
                                        }
                                        onClick={() => {
                                            push(secondaryNavigationTab.path);
                                            setPrimaryNavigation(secondaryNavigationTab.value);
                                        }}
                                        title={secondaryNavigationTab.title}
                                    />
                                ),
                            )}
                        </Tabs>
                    </Box>
                    <Box
                        flexBasis="33%"
                        alignItems="center"
                        justifyContent="flex-end"
                        display="flex"
                        sx={{
                            position: 'relative',
                            gap: 1.5,
                        }}
                    >
                        {primaryOrganization ? (
                            <Link
                                to={`/organizations/${primaryOrganization.uuid}`}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Avatar
                                    variant="rounded"
                                    src={primaryOrganization.logo_url || ''}
                                    alt={primaryOrganization.name}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        cursor: 'pointer',
                                        bgcolor: primaryOrganization.logo_url
                                            ? 'transparent'
                                            : 'rgba(255,255,255,0.15)',
                                    }}
                                >
                                    {!primaryOrganization.logo_url && (
                                        <Typography
                                            sx={{
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                            }}
                                        >
                                            {primaryOrganization.name?.[0] || ''}
                                        </Typography>
                                    )}
                                </Avatar>
                            </Link>
                        ) : hasMultipleOrgs ? (
                            <Box
                                ref={orgPickerAnchorRef}
                                onClick={() => setOrgPickerOpen((prev) => !prev)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                }}
                            >
                                <BusinessOutlinedIcon
                                    sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }}
                                />
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.85)',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Select Primary Org
                                </Typography>
                            </Box>
                        ) : null}
                        {orgPickerOpen && (
                            <ClickAwayListener onClickAway={() => setOrgPickerOpen(false)}>
                                <Popper
                                    open={orgPickerOpen}
                                    anchorEl={orgPickerAnchorRef.current}
                                    placement="bottom-start"
                                    sx={{ zIndex: 1300 }}
                                    transition
                                >
                                    {({ TransitionProps }) => (
                                        <Fade {...TransitionProps} timeout={200}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    minWidth: 240,
                                                    maxHeight: 320,
                                                    overflowY: 'auto',
                                                    bgcolor: 'rgba(20, 30, 60, 0.92)',
                                                    backdropFilter: 'blur(16px)',
                                                    border: '1px solid rgba(255,255,255,0.15)',
                                                    borderRadius: 2.5,
                                                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                                    p: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 1,
                                                        px: 1.5,
                                                        pt: 1,
                                                        pb: 0.5,
                                                    }}
                                                >
                                                    Select Organization
                                                </Typography>
                                                {selectableOrganizations.map((org) => (
                                                    <Box
                                                        key={org.uuid}
                                                        onClick={() =>
                                                            handleSelectOrganization(org.uuid)
                                                        }
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            px: 1.5,
                                                            py: 1,
                                                            borderRadius: 1.5,
                                                            cursor: 'pointer',
                                                            transition: 'background 0.15s',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                            },
                                                        }}
                                                    >
                                                        <Avatar
                                                            variant="rounded"
                                                            src={org.logo_url || ''}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                bgcolor: 'rgba(255,255,255,0.12)',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 600,
                                                                color: 'white',
                                                            }}
                                                        >
                                                            {!org.logo_url && (org.name?.[0] || '')}
                                                        </Avatar>
                                                        <Typography
                                                            sx={{
                                                                color: 'white',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 500,
                                                                flex: 1,
                                                            }}
                                                        >
                                                            {org.name}
                                                        </Typography>
                                                        {primaryOrganizationUuid === org.uuid && (
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    color: '#4caf50',
                                                                    fontSize: 18,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Paper>
                                        </Fade>
                                    )}
                                </Popper>
                            </ClickAwayListener>
                        )}
                        {shouldPromptPrimarySport && <PrimarySportQuickSelect />}
                        <ProfileOptions ref={profileOptionsRef} />
                        <Box
                            onClick={(e) => profileOptionsRef.current?.openMenu(e)}
                            sx={{
                                textAlign: 'left',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                transition: 'background 0.15s',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    lineHeight: 1.3,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {userName}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.65)',
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.3,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {userRoleLabel}
                            </Typography>
                        </Box>
                    </Box>
                </Toolbar>
                <SecondaryNavigation />
            </AppBar>
        </>
    );
};

export default DesktopTopNavigation;
